import {
  CompanyLevelFlex,
  NccVerifiedStatusIcon,
  type Company,
} from '@entities/company';
import { CellSort, DataTable, EmptyRowsFallback, StatusBage } from '@shared/ui';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { type SortQuery } from '@shared/types';
import { formatRatingValue } from '@shared/utils';
import { useFeatureFlag } from '@packages/feature-flags/react';
import dayjs from 'dayjs';
import { joinStringWithFallback } from '@shared/helpers';

export type Companies = Company[];

type CompanyListProps = {
  companies: Companies;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  recordsPerPageOptions?: number[];
  onRecordsPerPageChange?: (pageSize: number) => void;
  setPageSize: (pageSize: number) => void;
  isFilledFilters: boolean;
  onSortChange: (field: string, direction?: 'asc' | 'desc') => void;
  currentSort: SortQuery;
  isLoading: boolean;
  totalRecords?: number;
};

export const CompanyList = ({
  companies,
  page,
  setPage,
  pageSize,
  recordsPerPageOptions,
  onRecordsPerPageChange,
  setPageSize,
  isFilledFilters,
  onSortChange,
  currentSort,
  isLoading,
  totalRecords,
}: CompanyListProps) => {
  const navigate = useNavigate();
  const ratingsAndReviewsEnabled = useFeatureFlag('ratingsAndReviews');

  const columns = useMemo(
    () => [
      {
        accessor: 'id',
        title: (
          <CellSort
            label='ID'
            onSort={dir => onSortChange('id', dir)}
            currentDirection={currentSort.id}
          />
        ),
      },
      {
        accessor: 'name',
        title: (
          <CellSort
            label='Компания'
            onSort={dir => onSortChange('name', dir)}
            currentDirection={currentSort.name}
          />
        ),
      },
      {
        accessor: 'createdAt',
        title: (
          <CellSort
            label='Дата создания'
            onSort={dir => onSortChange('createdAt', dir)}
            currentDirection={currentSort.createdAt}
          />
        ),
        render: ({ createdAt }: Company) =>
          createdAt ? dayjs(createdAt).format('DD.MM.YYYY HH:mm') : '-',
      },
      {
        accessor: 'city.name',
        title: (
          <CellSort
            label='Город'
            onSort={dir => onSortChange('city.name', dir)}
            currentDirection={currentSort['city.name']}
          />
        ),
        render: ({ city }: Company) => {
          if (!city?.name) {
            return '-';
          }

          return city.name;
        },
      },
      {
        accessor: 'economicActivities',
        title: (
          <CellSort
            label='Сфера экономической деятельности'
            onSort={dir => onSortChange('economicActivities.name', dir)}
            currentDirection={currentSort['economicActivities.name']}
          />
        ),
        render: ({ economicActivities }: Company) =>
          joinStringWithFallback(
            economicActivities.map(activity => activity.name),
            { fallback: '-', separator: ', ' }
          ),
      },
      {
        accessor: 'manager.id',
        title: (
          <CellSort
            label='Менеджер'
            onSort={dir => onSortChange('manager.profile.fullName', dir)}
            currentDirection={currentSort['manager.profile.fullName']}
          />
        ),
        render: ({ manager }: Company) =>
          joinStringWithFallback(
            [manager.profile.firstName, manager.profile.lastName],
            { fallback: '-' }
          ),
      },
      {
        accessor: 'nccVerified',
        title: (
          <CellSort
            label='NCC подтвержден'
            onSort={dir => onSortChange('nccVerified', dir)}
            currentDirection={currentSort.nccVerified}
          />
        ),
        render: ({ nccVerified }: Company) => (
          <NccVerifiedStatusIcon nccVerified={nccVerified} />
        ),
      },
      ...(ratingsAndReviewsEnabled
        ? [
            {
              accessor: 'rating',
              title: (
                <CellSort
                  label='Рейтинг'
                  onSort={dir => onSortChange('rating', dir)}
                  currentDirection={currentSort.rating}
                />
              ),
              render: ({ rating, level }: Company) => {
                if (rating == null) {
                  return '-';
                }

                return (
                  <CompanyLevelFlex
                    level={level}
                    w='fit-content'
                    px='xs'
                    py={4}
                    style={{
                      borderRadius: 'var(--mantine-radius-sm)',
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                    justify='center'
                  >
                    {formatRatingValue(rating)}
                  </CompanyLevelFlex>
                );
              },
            },
          ]
        : []),
      {
        accessor: 'isActive',
        title: (
          <CellSort
            label='Статус'
            onSort={dir => onSortChange('isActive', dir)}
            currentDirection={currentSort.isActive}
          />
        ),
        render: ({ isActive }: Company) => <StatusBage isActive={isActive} />,
      },
    ],
    [ratingsAndReviewsEnabled, currentSort, onSortChange]
  );

  const handleRowClick = ({ record }: { record: Company }) => {
    navigate(`/company-list/${record.id}`);
  };

  const emptyState = useMemo(() => {
    if (isFilledFilters) {
      return {
        desc: 'К сожалению, совпадений не найдено',
        subDesc:
          'Попробуйте изменить параметры фильтров. Чтобы вернуться ко всему списку, сбросьте фильтр.',
      };
    }
    return {
      desc: 'Список компаний пуст',
      subDesc: 'Чтобы создать компанию, нажмите кнопку «Создать компанию»',
    };
  }, [isFilledFilters]);

  return (
    <DataTable<Company>
      fetching={isLoading}
      columns={columns}
      records={companies}
      onRowClick={handleRowClick}
      page={page}
      setPage={setPage}
      recordsPerPageOptions={recordsPerPageOptions}
      onRecordsPerPageChange={onRecordsPerPageChange}
      pageSize={pageSize}
      setPageSize={setPageSize}
      recordsPerPage={pageSize}
      onPageChange={setPage}
      totalRecords={totalRecords}
      emptyState={
        <EmptyRowsFallback
          desc={emptyState.desc}
          subDesc={emptyState.subDesc}
        />
      }
      highlightOnHover
    />
  );
};
